<template>
  <div class="mt-4">
    <div v-if="payables.length == 0">
      <h2 class="secondary--text pa-3">
        {{
          $t(
            "people:ShoppingBasket.EmptyBasket.explanation",
            "Shopping basket is empty",
            "Explanation in case the shopping basket is empty"
          )
        }}
      </h2>
    </div>
    <div v-else>
      <v-sheet
        v-for="(payable, i) in payables"
        :key="payable.id + '-' + i"
        color="strong"
        data-test="orderCard"
        elevation="4"
        class="mb-10"
        :class="{
          'card-pointer': active != i && !payable.disabled,
          'card-disallowed': payable.disabled,
          ['p' + ($vuetify.breakpoint.mdAndUp ? 'a' : 'y') + '-5']: true
        }"
        @click="
          evt =>
            selectCard(evt, {
              index: i,
              payable
            })
        "
      >
        <div
          :class="{
            'not-selected-group': active != i,
            'px-3': $vuetify.breakpoint.mdAndUp
          }"
        >
          <div class="d-flex flex-row mb-3">
            <div
              class="rounded-radio d-flex"
              :class="$vuetify.breakpoint.lgAndUp ? 'pr-4' : 'px-4'"
            >
              <button
                :class="[
                  active == i ? 'rounded-radio_content_active' : '',
                  'rounded-radio_content'
                ]"
                :disabled="
                  payable.disabled ||
                  selectionCardDisabled(i) ||
                  selectionsFreezed(payable, i)
                    ? 'disabled'
                    : undefined
                "
                data-test="selectPayable"
                :style="
                  payable.disabled ||
                  selectionCardDisabled(i) ||
                  selectionsFreezed(payable, i)
                    ? 'cursor: not-allowed;'
                    : 'cursor: pointer;'
                "
              />
            </div>
            <div class="d-flex align-center" style="width: 100%">
              <div v-if="payable.entityType == 'Event'" style="width: 100%">
                <AppShowEventDetails
                  link
                  :event="payable.event"
                  :data-test="payable.event.name"
                />
              </div>
              <div v-else-if="payable.entityType == 'Federation'">
                <h3>
                  {{
                    $t(
                      "people:ShoppingBasket.PayableGroup.FederationServices.title",
                      "Federation services",
                      "Name of payable group used for marking of horse licences and other federation services"
                    )
                  }}
                </h3>
              </div>
              <div v-else-if="payable.entityType == 'Organisation'">
                <h3>
                  {{ payable.payment.recipientOrganisation.name }}
                </h3>
              </div>
              <div
                v-else-if="payable.entityType == 'Invoice'"
                style="width: 100%"
              >
                <div class="d-flex" style="align-items: center">
                  <div style="flex-grow: 1">
                    <h3 class="d-flex" style="flex-wrap: wrap">
                      {{
                        $t(
                          "people:shoppingBasket.shoppingBasketTab.invoiceComponent.invoice.title",
                          "Invoice:"
                        )
                      }}
                      <div
                        class="d-flex"
                        :class="{
                          'px-2': $vuetify.breakpoint.smAndUp
                        }"
                      >
                        <template v-if="payable.invoice">{{
                          payable.invoice.invoiceNumberToDisplay
                        }}</template>

                        <AppShowInvoiceStatus
                          :invoice="payable.invoice"
                          hide-timestamp
                          class="px-2"
                        />
                      </div>
                    </h3>
                  </div>
                  <div
                    v-if="
                      payable.entityType == 'Invoice' &&
                      $allowedMutation(
                        payable.invoice.allowedMutations,
                        'invoiceGenerateFile'
                      )
                    "
                    class="d-flex justify-end"
                  >
                    <AppShowInvoicePdfs
                      format="button"
                      :shopping-basket-id="shoppingBasketId"
                      :invoice="payable.invoice"
                    >
                      <v-icon left>$iconReceipt</v-icon
                      >{{
                        $t(
                          "people:shoppingBasket.shoppingBasketTab.invoiceComponent.pdfFile.text",
                          "invoice pdf."
                        )
                      }}
                    </AppShowInvoicePdfs>
                  </div>
                </div>

                <AppShowOrganisationName
                  v-if="payable.invoice.sellerOrganisation"
                  class="muted--text"
                  :organisation="payable.invoice.sellerOrganisation"
                />

                <div v-if="payable.invoice.status == 'OPEN'" class="mt-5">
                  <span v-if="payable.invoice.issueDate">
                    {{
                      $t(
                        "people:shoppingBasket.shoppingBasketTab.invoiceComponent.issuedOn.label",
                        "Issued on:"
                      )
                    }}
                    {{ $dayjs.date(payable.invoice.issueDate) }}
                    |
                  </span>
                  {{
                    $t(
                      "people:shoppingBasket.shoppingBasketTab.invoiceComponent.paymentDueDate.label",
                      "Due date:"
                    )
                  }}
                  <span
                    :class="{
                      'red--text': $dayjs($dayjs()).isAfter(
                        payable.invoice.paymentDueDate
                      )
                    }"
                    >{{ $dayjs.date(payable.invoice.paymentDueDate) }}</span
                  >
                  |
                  {{
                    $t(
                      "people:shoppingBasket.shoppingBasketTab.invoiceComponent.remittanceCode.label",
                      "Remittance information:"
                    )
                  }}
                  {{ payable.invoice.remittanceCodeToDisplay }}
                </div>
                <div v-else-if="payable.invoice.status == 'DRAFT'">
                  {{
                    $t(
                      [
                        "people:shoppingBasket.shoppingBasketTab.invoiceComponent.draftInformationNote.text",
                        "Draft invoice is a preview of your invoice that will be issued on {\{issueDate}\}. Please note: new invoiced items will be added under draft invoice during the current month. Please check and modify the billing contact. If you have any questions regarding items in the invoice or requests to modify billing contacts, please contact our technical support"
                      ],
                      {
                        issueDate: $dayjs.date(payable.invoice.issueDate),
                        interpolation: { escapeValue: false }
                      }
                    )
                  }}
                </div>
              </div>
            </div>
          </div>

          <OrderTable
            :ref="`orderTable-${i}`"
            :shopping-basket-id="shoppingBasketId"
            :payable="payable"
            :payable-sections="payable.payableSections"
            :entity-type="payable.entityType"
            :refundable="payable.availableRefundsCents"
            :disabled="active != i || loading == i"
            :frozen="selectionsFreezed(payable, i)"
            @selectionUpdate="
              payableItemIds => (selected = { ids: payableItemIds, index: i })
            "
            @refetch="() => $emit('refetch')"
          />

          <div
            v-if="showBillingAndTermsSection(payable, i)"
            data-test="billingAndTerms"
          >
            <AppBillingContact
              :ref="'billingContact-' + i"
              :contact-object="billingContactGetObject(payable)"
              :person-id="person.id"
              :contact-id="billingContactGetId(payable)"
              :contactable-id="billingContactContactableId(payable)"
              :contactable-type="billingContactContactableType(payable)"
              :disabled="billingContactIsDisabled(payable, i)"
              :force-cols="$vuetify.breakpoint.smAndDown ? 12 : undefined"
              :content-padding="
                $vuetify.breakpoint.lgAndUp ? undefined : 'px-7'
              "
              @set-contact="contact => setPaymentBuyerContact(contact, i)"
              @submitted-contact="
                contact => updateInvoiceBuyerContact(payable, contact)
              "
            >
              <template #heading="{ editButtonAttrs }">
                <v-sheet
                  color="primary"
                  class="white--text fipa--header px-0"
                  style="height: 100%"
                  :class="{
                    'py-2': !$vuetify.breakpoint.lgAndUp
                  }"
                  tile
                >
                  <v-row no-gutters class="px-5 py-2">
                    <v-col class="d-flex" style="align-self: center">
                      <b>{{
                        $t(
                          "people:shoppingBasket.shoppingBasketTab.invoiceComponent.payerOrInvoiceReceiver.subheading",
                          "Payer / invoice receiver"
                        )
                      }}</b>
                    </v-col>

                    <v-col class="text-right">
                      <v-btn
                        v-if="editButtonAttrs.renderable()"
                        v-bind="modifyEditButtonAttrs(editButtonAttrs)"
                        class="edit-green"
                        small
                        v-on="editButtonAttrs.on"
                      >
                        <v-icon color="white" small>$edit</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-sheet>
              </template>
              <template #hint><span /></template>
            </AppBillingContact>

            <v-col
              v-if="checkoutMode == i && !!preparePaymentForPayables"
              class="px-2"
            >
              <div
                v-if="
                  preparePaymentForPayables.vatRule &&
                  preparePaymentForPayables.vatRule != 'NON_TAXABLE'
                "
              >
                <strong class="dynamic-title-size">{{
                  returnVatRuleText(preparePaymentForPayables.vatRule)
                }}</strong>
              </div>

              <v-card color="white" class="mt-4 mb-5 py-4 px-2" tile>
                <v-row dense>
                  <v-col cols="8" md="10" class="text-right">
                    <div class="dynamic-title-size">
                      <b>
                        {{
                          $t(
                            "people:shoppingBasket.shoppingBasketTab.checkout.subtotal.label",
                            "Subtotal:"
                          )
                        }}</b
                      >
                    </div>
                  </v-col>
                  <v-col cols="4" md="2" class="text-right">
                    <div
                      class="accent--text mr-4 dynamic-title-size"
                      style="white-space: nowrap"
                    >
                      <b>
                        {{
                          masterCurrency(
                            preparePaymentForPayables.amountNetCents
                          )
                        }}</b
                      >
                    </div>
                  </v-col>

                  <v-col cols="8" md="10" class="text-right">
                    <div class="dynamic-title-size">
                      <b>{{
                        $t(
                          "people:shoppingBasket.shoppingBasketTab.checkout.VATTotalAmount.label",
                          "VAT total amount:"
                        )
                      }}</b>
                    </div>
                  </v-col>
                  <v-col cols="4" md="2" class="text-right">
                    <div
                      class="accent--text mr-4 dynamic-title-size"
                      style="white-space: nowrap"
                    >
                      <b>{{
                        masterCurrency(preparePaymentForPayables.amountVatCents)
                      }}</b>
                    </div>
                  </v-col>

                  <v-col cols="8" md="10" class="text-right">
                    <div class="dynamic-title-size">
                      <b>{{
                        $t(
                          "people:shoppingBasket.shoppingBasketTab.checkout.totalToPay.label",
                          "Total to pay:"
                        )
                      }}</b>
                    </div>
                  </v-col>
                  <v-col cols="4" md="2" class="text-right">
                    <div
                      class="accent--text mr-4 dynamic-title-size"
                      style="white-space: nowrap"
                    >
                      <b>{{
                        masterCurrency(preparePaymentForPayables.amountCents)
                      }}</b>
                    </div>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>

            <div v-if="$vuetify.breakpoint.lgAndUp">
              <v-sheet
                color="primary"
                class="fipa--header mb-4 white--text"
                tile
              >
                <div
                  class="d-flex flex-row px-2 align-center justify-space-between"
                  style="height: 100%"
                >
                  <div>
                    <b>
                      <template v-if="payable.entityType == 'Invoice'">
                        {{
                          $t(
                            "people:shoppingBasket.shoppingBasketTab.invoiceComponent.termsOfUse.subheading",
                            "Terms of use",
                            "Title for the terms and conditions section"
                          )
                        }}
                      </template>
                      <template v-else>
                        {{
                          $t(
                            "people:ShoppingBasket.TermsAndConditionsSection.title",
                            "Terms of use",
                            "Title for the terms and conditions section"
                          )
                        }}
                      </template>
                    </b>
                  </div>
                </div>
              </v-sheet>
              <div class="px-3" data-test="terms">
                <template v-if="payable.entityType == 'Invoice'">
                  <div
                    class="my-2"
                    v-html="
                      $t(
                        'people:shoppingBasket.shoppingBasketTab.invoiceComponent.consentWithTermsOfUse.subheading',
                        'Consent with terms of use'
                      )
                    "
                  ></div>
                </template>
                <div
                  v-else
                  class="my-2"
                  v-html="
                    $t(
                      'people:ShoppingBasket.TermsAndConditionsSection.heading',
                      'General terms of use',
                      'Heading for the terms and conditions section'
                    )
                  "
                ></div>

                <v-checkbox
                  v-if="!payable.disabled"
                  :value="givenConsent[i]"
                  :error-messages="consentError"
                  :disabled="loading == i"
                  data-test="orderConsent"
                  class="d-flex"
                  color="primary"
                  @change="v => (givenConsent[i] = v)"
                >
                  <template #label>
                    <div
                      v-if="payable.entityType == 'Invoice'"
                      v-html="
                        $t(
                          'people:shoppingBasket.shoppingBasketTab.invoiceComponent.consentText.checkbox.label',
                          'I have read and understood the Terms of Use and give my consent. '
                        )
                      "
                    ></div>
                    <div
                      v-else
                      v-html="
                        $t(
                          'people:ShoppingBasket.TermsAndConditionsSection.Consent.label'
                        )
                      "
                    ></div>
                  </template>
                </v-checkbox>
              </div>
            </div>
            <template v-else>
              <v-divider class="my-5" />
              <div class="px-7">
                <div data-test="terms" style="font-size: 14px">
                  <template v-if="payable.entityType == 'Invoice'">
                    <div
                      class="my-2"
                      v-html="
                        $t(
                          'people:shoppingBasket.shoppingBasketTab.invoiceComponent.consentWithTermsOfUse.subheading',
                          'Consent with terms of use'
                        )
                      "
                    ></div>
                  </template>
                  <div
                    v-else
                    class="my-2"
                    v-html="
                      $t(
                        'people:ShoppingBasket.TermsAndConditionsSection.heading',
                        'General terms of use',
                        'Heading for the terms and conditions section'
                      )
                    "
                  ></div>

                  <v-checkbox
                    v-if="!payable.disabled"
                    :value="givenConsent[i]"
                    :error-messages="consentError"
                    :disabled="loading == i"
                    data-test="orderConsent"
                    color="primary"
                    @change="v => (givenConsent[i] = v)"
                  >
                    <template #label>
                      <div
                        v-if="payable.entityType == 'Invoice'"
                        v-html="
                          $t(
                            'people:shoppingBasket.shoppingBasketTab.invoiceComponent.consentText.checkbox.label',
                            'I have read and understood the Terms of Use and give my consent. '
                          )
                        "
                      ></div>
                      <div
                        v-else
                        v-html="
                          $t(
                            'people:ShoppingBasket.TermsAndConditionsSection.Consent.label'
                          )
                        "
                      ></div>
                    </template>
                  </v-checkbox>
                </div>
              </div>
            </template>
          </div>
          <div
            :class="[$vuetify.breakpoint.smAndDown ? 'px-3' : '', 'text-right']"
          >
            <br />
            <v-row dense>
              <v-col cols="12" md="8">
                <AppAlertNotice
                  v-show="errorInfo.length > 0 && i == selectedToPay.index"
                  class="alert pa-4"
                >
                  <template v-for="(info, y) in errorInfo">
                    <div :key="y" class="mb-1">{{ info }}</div>
                  </template>
                </AppAlertNotice>
              </v-col>
              <v-col cols="12" md="4" offset-md="8">
                <AppAlertNotice
                  v-if="payable.disabled && payable.disableReason"
                  class="mb-3"
                  ignore-aria
                  >{{ displayText(payable.disableReason) }}</AppAlertNotice
                >
              </v-col>
            </v-row>
            <v-row v-if="!payable.disabled" dense>
              <v-col
                cols="12"
                md="4"
                offset-md="8"
                :class="$vuetify.breakpoint.mdAndDown ? 'px-5' : ''"
                class="d-md-flex justify-end"
              >
                <template
                  v-if="payable.twoStepCheckout == true && checkoutMode != i"
                >
                  <v-btn
                    color="accent"
                    large
                    data-test="checkout"
                    :class="[
                      $vuetify.breakpoint.mdAndUp ? 'dialog-button' : '',
                      'ma-2 entsys--btn--round'
                    ]"
                    :disabled="payable.disabled || active != i"
                    :block="$vuetify.breakpoint.smAndDown"
                    style="min-width: 16em"
                    @click="() => checkoutPurchase(i)"
                  >
                    {{ $t("common:checkout.button", "Check out") }}
                  </v-btn>
                </template>
                <template v-else>
                  <template
                    v-if="offsiteForm.paymentProcessor != 'STRIPE_CONNECT'"
                  >
                    <v-btn
                      v-if="
                        payable.twoStepCheckout == true && checkoutMode == i
                      "
                      data-test="cancelCheckout"
                      large
                      :block="$vuetify.breakpoint.smAndDown"
                      rounded
                      :loading="
                        $apollo.queries.preparePaymentForPayables.loading
                      "
                      outlined
                      :class="{
                        'dialog-button': $vuetify.breakpoint.mdAndUp,
                        'ma-2': $vuetify.breakpoint.mdAndUp,
                        'mb-5': $vuetify.breakpoint.smAndDown
                      }"
                      @click="() => cancelCheckoutPurchase()"
                    >
                      {{
                        $t("common:cancelCheckout.button", "Cancel checkout")
                      }}
                    </v-btn>
                    <v-btn
                      color="accent"
                      large
                      rounded
                      data-test="purchase"
                      :class="{
                        'dialog-button': $vuetify.breakpoint.mdAndUp,
                        'ma-2': $vuetify.breakpoint.mdAndUp
                      }"
                      :loading="
                        loading == i ||
                        $apollo.queries.preparePaymentForPayables.loading
                      "
                      :disabled="
                        paymentIniateForInvoiceDisabled(payable) ||
                        payable.disabled ||
                        active != i
                      "
                      :block="$vuetify.breakpoint.smAndDown"
                      @click="() => commitPurchase(payable)"
                    >
                      <template v-if="payable.entityType == 'Invoice'">
                        {{
                          $t(
                            "people:shoppingBasket.shoppingBasketTab.invoiceComponent.pay.button",
                            "Pay for invoice"
                          )
                        }}
                      </template>
                      <template v-else>
                        {{
                          $t(
                            "people:ShoppingBasket.button.InitiatePayment.label",
                            "Purchase",
                            "Label for the button to initiate the payment"
                          )
                        }}
                      </template>
                    </v-btn>
                  </template>
                  <v-btn
                    v-if="
                      payable.availableRefundsCents > 0 &&
                      $allowedMutation(
                        person.allowedMutations,
                        'payoutInitiateFromEventToPerson'
                      )
                    "
                    color="accent"
                    large
                    :class="[
                      $vuetify.breakpoint.mdAndUp ? 'dialog-button' : '',
                      'ma-2 entsys--btn--round'
                    ]"
                    :loading="loading == i"
                    :disabled="payable.disabled || active != i"
                    :block="$vuetify.breakpoint.smAndDown"
                    style="min-width: 16em"
                    @click="() => commitRefundPayout(payable.event.id)"
                  >
                    {{
                      $t(
                        "people:ShoppingBasket.button.InitiateRefundsPayout.label",
                        "Request refunds payout",
                        "Label for the button to request refunds payout"
                      )
                    }}
                  </v-btn>
                </template>
              </v-col>
            </v-row>
          </div>
        </div>
        <form
          v-if="!payable.disabled && active == i"
          id="formSubmit"
          :action="offsiteForm.target"
          method="post"
          style="display: none"
        >
          <input
            v-for="(value, key) in offsiteForm.pmt"
            :key="key"
            :name="key"
            :value="value"
            type="hidden"
            readonly
          />
        </form>
        <PaymentStripeAnyMethod
          v-if="
            !payable.disabled &&
            active == i &&
            offsiteForm.paymentProcessor == 'STRIPE_CONNECT' &&
            offsiteForm.clientSecret
          "
          :payment="payable.payment"
          :payment-id="offsiteForm.paymentId"
          :client-secret="offsiteForm.clientSecret"
          :shopping-basket-id="shoppingBasketId"
          :amount-cents="offsiteForm.amountCents"
          :on-behalf-of-connected-stripe-account-id="
            offsiteForm.onBehalfOfConnectedStripeAccountId
          "
          @paymentSuccessful="() => handlePaymentSuccess()"
          @paymentFailed="evt => handlePaymentFailure(evt)"
          @closeForm="() => closePayment()"
        />
      </v-sheet>
    </div>
  </div>
</template>
<script>
import { defineComponent } from "vue";
import gql from "fraql";
import { useDisplayText } from "@composables/display_text";
import { get, clone } from "lodash-es";
import AppAlertNotice from "@elements/AppAlertNotice/index.vue";
import AppShowEventDetails from "@AppShows/event/AppShowEventDetails.vue";

import OrderTable from "./elements/OrderTable.vue";

import AppShowOrganisationName from "@AppShows/organisation/AppShowOrganisationName.vue";
import AppShowInvoicePdfs from "@AppShows/AppShowInvoicePdfs.vue";
import PaymentStripeAnyMethod from "./elements/PaymentStripeAnyMethod.vue";
import AppBillingContact, {
  prepContactForBillingInput,
  contactDataIsSame
} from "@elements/billing/AppBillingContact.vue";
import AppShowInvoiceStatus from "@AppShows/AppShowInvoiceStatus.vue";
import { useCurrency } from "@composables/masterdata/config";

export default defineComponent({
  name: "OrderBasket",
  components: {
    OrderTable,
    AppAlertNotice,
    AppShowEventDetails,
    AppShowOrganisationName,
    AppShowInvoicePdfs,
    PaymentStripeAnyMethod,
    AppBillingContact,
    AppShowInvoiceStatus
  },
  props: {
    payables: {
      type: Array,
      default: () => []
    },
    person: {
      type: Object,
      required: true
    },
    shoppingBasketId: {
      type: String,
      default: null
    }
  },
  setup() {
    const { displayText } = useDisplayText();
    return { ...useCurrency(), displayText };
  },
  data() {
    return {
      checkoutMode: null,
      preparePaymentForPayables: null,
      active: 0,
      paymentBuyerContacts: {},
      givenConsent: {},
      consentError: [],
      selectedToPay: { ids: [], index: null },
      loading: null,
      errorInfo: [],
      offsiteForm: {
        target: "#",
        pmt: {},
        clientSecret: null,
        paymentId: null,
        paymentProcessor: null,
        amountCents: 0,
        onBehalfOfConnectedStripeAccountId: null
      }
    };
  },
  computed: {
    selected: {
      get() {
        return this.selectedToPay;
      },
      set(val) {
        this.selectedToPay = val;
      }
    }
  },
  watch: {
    active(newIndex) {
      this.$refs[`orderTable-${newIndex}`][0].preselect(
        this.payables[newIndex].payableSections
      );
    }
  },
  created() {
    for (let i = 0; this.payables.length > i; i++) {
      this.givenConsent[i] = false;
    }
  },
  mounted() {
    // First one's items are preselected
    if (this.$refs["orderTable-0"] && this.$refs["orderTable-0"][0].preselect) {
      this.$refs["orderTable-0"][0].preselect(this.payables[0].payableSections);
    }
  },
  apollo: {
    preparePaymentForPayables: {
      query: gql`
        query preparePaymentForPayables(
          $buyerContact: ContactForBillingInput
          $payableIds: [ID!]!
          $personId: ID
          $shoppingBasketId: ID
        ) {
          preparePaymentForPayables(
            buyerContact: $buyerContact
            payableIds: $payableIds
            personId: $personId
            shoppingBasketId: $shoppingBasketId
          ) {
            id
            buyerContact {
              id
              ${AppBillingContact.fragments.contactFields}
            }
            vatRule
            amountCents
            amountNetCents
            amountVatCents
          }
        }
      `,
      skip() {
        return this.checkoutMode == null || this.checkoutMode != this.active; // only run if checkoutMode has value defined and its number is the same as the selected card's index
      },
      variables() {
        return {
          buyerContact: prepContactForBillingInput(
            this.paymentBuyerContacts[this.checkoutMode]
          ),
          payableIds: this.selectedToPay.ids,
          personId: this.person.id,
          shoppingBasketId: this.shoppingBasketId
        };
      }
    }
  },
  methods: {
    paymentIniateForInvoiceDisabled(payable) {
      if (payable.entityType == "Invoice") {
        return !this.$allowedMutation(
          payable.payment.allowedMutations,
          "paymentInitiateForInvoice"
        );
      }
      return false;
    },
    returnVatRuleText(vatRule) {
      switch (vatRule) {
        case "TAXABLE":
          return this.$t(
            "people:shoppingBasket.shoppingBasketTab.VATRule.TAXABLE.label",
            "VAT taxable sales"
          );
        case "EXPORT_EXEMPT":
          return this.$t(
            "people:shoppingBasket.shoppingBasketTab.VATRule.EXPORT_EXEMPT.label",
            "VAT exempt export"
          );
        case "REVERSE_CHARGE":
          return this.$t(
            "people:shoppingBasket.shoppingBasketTab.VATRule.REVERSE_CHARGE.label",
            "VAT reverse charge"
          );
      }
    },

    selectionCardDisabled(payableIndex) {
      // if checkoutMode has value and its not equal to its value, then return true to disable
      return this.checkoutMode != null && this.checkoutMode != payableIndex;
    },
    selectionsFreezed(payable, payableIndex) {
      // if payable has two step checkout and checkoutMode has value, assert its the same as given index to prevent changing/adding items
      if (payable.twoStepCheckout == true) {
        return (
          this.$apollo.queries.preparePaymentForPayables.loading ||
          this.checkoutMode == payableIndex
        );
      }
      return false;
    },
    showBillingAndTermsSection(payable, payableIndex) {
      if (payable.twoStepCheckout == true) {
        return this.checkoutMode == payableIndex;
      }
      return true;
    },

    modifyEditButtonAttrs(attrs) {
      const shallowCloneAttrs = clone(attrs);
      delete shallowCloneAttrs.renderable;
      delete shallowCloneAttrs.on;
      return shallowCloneAttrs;
    },

    billingContactGetObject(payable) {
      switch (payable.entityType) {
        case "Invoice":
          return payable.invoice?.buyerContact ?? null;
        default:
          return payable.payment?.buyerContact ?? null;
      }
    },

    billingContactGetId(payable) {
      switch (payable.entityType) {
        case "Invoice":
          return payable.invoice?.buyerContact?.id ?? null;
        default:
          return payable.payment?.buyerContact?.id ?? null;
      }
    },

    billingContactContactableId(payable) {
      switch (payable.entityType) {
        case "Invoice":
          return payable.invoice?.id ?? null;
        default:
          return payable.payment?.id ?? null;
      }
    },

    billingContactContactableType(payable) {
      switch (payable.entityType) {
        case "Invoice":
          return "Invoice";
        default:
          return "Payment";
      }
    },

    billingContactIsDisabled(payable, payableIndex) {
      if (
        payable.disabled ||
        this.active != payableIndex ||
        this.loading == payableIndex
      ) {
        return true;
      }
      if (payable.entityType == "Invoice") {
        return this.$allowedMutation(
          payable.invoice?.allowedMutations,
          "invoiceBuyerContactCreateOrUpdate"
        ) == true
          ? false // If it has, return false so it wont be disabled
          : true;
      }

      return false;
    },

    setPaymentBuyerContact(contact, payableIndex) {
      this.$set(
        this.paymentBuyerContacts,
        payableIndex,
        prepContactForBillingInput(contact)
      );
    },

    closePayment() {
      this.offsiteForm = {
        target: "#",
        pmt: {},
        clientSecret: null,
        paymentProcessor: null,
        amountCents: 0,
        onBehalfOfConnectedStripeAccountId: null
      };
      this.givenConsent[this.active] = false;
      this.loading = null;
      this.selectedToPay = { ids: [], index: 0 }; // clear out as its going to refetch it anyway
      this.$emit("refetch");
      this.$emit("initiatedPayment", false);
    },
    selectCard(evt, { index, payable }) {
      if (
        this.active == index ||
        payable.disabled ||
        this.selectionCardDisabled(index) ||
        this.selectionsFreezed(payable, index) ||
        this.loading != null
      ) {
        return null;
      }

      this.offsiteForm = {
        target: "#",
        pmt: {},
        clientSecret: null,
        paymentId: null,
        paymentProcessor: null,
        amountCents: 0,
        onBehalfOfConnectedStripeAccountId: null
      };

      this.givenConsent[index] = false;
      this.loading = null;
      this.selectedToPay = { ids: [], index };

      this.active = index;

      this.$nextTick(() => {
        if (this.$refs["billingContact-" + this.active]?.length > 0) {
          this.$refs[
            "billingContact-" + this.active
          ][0].validateContactFormManually({
            doNotAlertAutomatically: true
          });
        }
      });
    },
    validateRequirements(payable) {
      this.errorInfo = [];
      this.consentError = [];

      if (
        !payable.singlePaymentForWholeGroup &&
        this.selectedToPay.ids.length == 0
      ) {
        this.errorInfo.push(
          this.$t(
            "people:ShoppingBasket.feedbackMessage.pleaseSelectItems",
            "Please select items to pay"
          )
        );
      }

      if (!this.givenConsent[this.active]) {
        this.consentError.push(
          this.$t(
            "people:ShoppingBasket.feedbackMessage.consentRequired",
            "Required"
          )
        );
      }

      if (this.$refs["billingContact-" + this.active]?.length > 0) {
        const contactData = this.$refs[
          "billingContact-" + this.active
        ][0].validateContactFormManually({
          returnContactAndCloseForm: true
        });

        if (typeof contactData === "object") {
          this.setPaymentBuyerContact(contactData, this.active);
        } else if (contactData == false) {
          return false;
        }
      }

      return this.errorInfo.length + this.consentError.length == 0;
    },
    commitRefundPayout(eventId) {
      this.v$.form.$touch();
      if (this.v$.form.$invalid) {
        return false;
      }
      this.formError = {};
      this.loading = this.selectedToPay.index;

      this.$apollo
        .mutate({
          mutation: gql`
            mutation payoutInitiateFromEventToPerson(
              $eventId: ID!
              $personId: ID!
            ) {
              payoutInitiateFromEventToPerson(
                input: { eventId: $eventId, personId: $personId }
              ) {
                payout {
                  id
                }
              }
            }
          `,
          variables: {
            eventId: eventId,
            personId: this.person.id
          }
        })
        .then(() => {
          this.$emit("refetch");
          this.$emit("initiatedPayment", false);
          this.selectedToPay = { ids: [], index: 0 };
          this.loading = null;
        })
        .catch(err => {
          this.$root.$snackbarServerError(err, { level: "error" });
          this.loading = null;
        });
    },

    checkoutPurchase(payableIndex) {
      this.checkoutMode = payableIndex;
    },
    cancelCheckoutPurchase() {
      this.checkoutMode = null;
      this.preparePaymentForPayables = null;
    },

    async commitPurchase(payable) {
      if (payable.entityType == "Invoice") {
        this.paymentInitiateForInvoice(payable);
        return;
      }
      if (this.validateRequirements(payable) == false) return;

      this.loading = this.selectedToPay.index;
      const index = this.selectedToPay.index;

      this.$apollo
        .mutate({
          mutation: gql`
            mutation paymentInitiate(
              $payableIds: [ID!]!
              $personId: ID
              $shoppingBasketId: ID
              $buyerContact: ContactForBillingInput!
            ) {
              paymentInitiate(
                input: {
                  payableIds: $payableIds
                  personId: $personId
                  shoppingBasketId: $shoppingBasketId
                  buyerContact: $buyerContact
                }
              ) {
                payment {
                  id
                  referenceCode
                  amountCents
                }
                offsitePaymentFormFields
              }
            }
          `,
          variables: {
            payableIds: this.selectedToPay.ids,
            personId: this.person.id,
            shoppingBasketId: this.shoppingBasketId,
            buyerContact: this.paymentBuyerContacts[index]
          }
        })
        .then(({ data }) => {
          this.handlePaymentInitResponse(data.paymentInitiate);
        })
        .catch(err => {
          console.error("Catched an error", err);
          this.$emit("initiatedPayment", false);
          this.loading = null;
          //this.$root.$snackbar("error", { text: err });
          this.$root.$snackbarServerError(err, { level: "error" });
        });
    },
    startStripePayment({
      clientSecret: clientSecret,
      paymentId: paymentId,
      amountCents: amountCents,
      onBehalfOfConnectedStripeAccountId: onBehalfOfConnectedStripeAccountId
    }) {
      console.log("Payment was initiated with Stripe");

      // initiate PaymentStripeCard component
      this.offsiteForm.clientSecret = clientSecret;
      this.offsiteForm.paymentId = paymentId;
      this.offsiteForm.amountCents = amountCents;
      this.offsiteForm.onBehalfOfConnectedStripeAccountId =
        onBehalfOfConnectedStripeAccountId;
    },
    startSveaPayment(offsitePaymentFormFields) {
      console.log(
        "Payment was initiated with Svea (Maksuturva or Svea Direct)"
      );

      this.offsiteForm.target = offsitePaymentFormFields.target_url;
      const fieldsKeys = Object.keys(offsitePaymentFormFields);

      fieldsKeys.forEach(key => {
        // Ignore target_url
        if (key.includes("pmt")) {
          this.offsiteForm.pmt[key] = offsitePaymentFormFields[key];
        }
      });

      // Ensuring input elements are rendered
      this.$nextTick(() => {
        const form = this.$el.querySelector("#formSubmit");
        form.submit();
      });
    },
    handlePaymentSuccess() {
      this.$root.$snackbar("success");

      this.$emit("refetch");
      this.$emit("initiatedPayment", false);

      this.loading = null;
      this.selectedToPay = { ids: [], index: 0 };
      this.$emit("toHistory");
      this.$forceUpdate();
    },
    handlePaymentFailure() {
      this.$emit("initiatedPayment", false);
      this.loading = null;
      //this.$root.$snackbar("error", { text: message });
      //this.$root.$snackbarServerError(message, { level: "error" });
    },

    handlePaymentInitResponse(paymentMutation) {
      this.offsiteForm.paymentProcessor = get(
        paymentMutation,
        "offsitePaymentFormFields.paymentProcessor"
      );

      switch (this.offsiteForm.paymentProcessor) {
        case "STRIPE_CONNECT":
          this.startStripePayment({
            clientSecret: paymentMutation.offsitePaymentFormFields.clientSecret,
            paymentId: paymentMutation.payment.id,
            amountCents: paymentMutation.payment.amountCents,
            onBehalfOfConnectedStripeAccountId:
              paymentMutation.offsitePaymentFormFields
                .onBehalfOfConnectedStripeAccountId
          });
          break;

        case "MAKSUTURVA":
        case "SVEA_DIRECT":
          this.startSveaPayment(paymentMutation.offsitePaymentFormFields);
          break;

        default:
          console.log("Payment was done using returns");
          this.handlePaymentSuccess();
      }
    },

    async paymentInitiateForInvoice(payable) {
      if (this.validateRequirements(payable) == false) return;

      this.loading = this.selectedToPay.index;

      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation paymentInitiateForInvoice($invoiceId: ID!) {
              paymentInitiateForInvoice(input: { invoiceId: $invoiceId }) {
                payment {
                  id
                  referenceCode
                  amountCents
                }
                offsitePaymentFormFields
              }
            }
          `,
          variables: {
            invoiceId: payable.invoice.id
          }
        });

        this.handlePaymentInitResponse(data.paymentInitiateForInvoice);
      } catch (error) {
        this.$root.$snackbarServerError(error, { level: "error" });
      } finally {
        this.loading = null;
      }
    },

    async updateInvoiceBuyerContact(payable, contact) {
      if (payable.entityType != "Invoice") return;
      if (contactDataIsSame(payable.invoice.buyerContact, contact)) {
        return;
      }

      contact = prepContactForBillingInput(contact);
      this.loading = this.selectedToPay.index;

      try {
        await this.$apollo.mutate({
          mutation: gql`
            mutation invoiceBuyerContactCreateOrUpdate(
              $invoiceId: ID!
              $buyerContact: ContactForBillingInput!
            ) {
              invoiceBuyerContactCreateOrUpdate(
                input: { id: $invoiceId, buyerContact: $buyerContact }
              ) {
                invoice {
                  id
                  ${this.$options.fragments.invoiceFields}
                }
              }
            }
          `,
          variables: {
            invoiceId: payable.invoice.id,
            buyerContact: contact
          }
        });

        this.$emit("refetch");
      } catch (error) {
        this.$root.$snackbarServerError(error, { level: "error" });
      } finally {
        this.loading = null;
      }
    }
  },
  fragments: {
    payabeSectionProductFields: gql`
      fragment _ on Product {
        id
        ${OrderTable.fragments.payabeSectionProductFields}
      }
    `,
    payableFields: gql`
      fragment _ on Payable {
        ${OrderTable.fragments.payableFields}
      }
    `,
    personPayableGroupFields: gql`
      fragment _ on PersonPayableGroup {
        twoStepCheckout
        singlePaymentForWholeGroup
        ${OrderTable.fragments.personPayableGroupFields}
      }
    `,
    entryFields: gql`
      fragment _ on Entry {
        ${OrderTable.fragments.entryFields}
      }
    `,
    invoiceLineFields: gql`
      fragment _ on InvoiceLine {
        ${OrderTable.fragments.invoiceLineFields}
      }
    `,
    invoiceFields: gql`
      fragment _  on Invoice {
        id
        status
        ${AppShowInvoiceStatus.fragments.invoiceFields}
        issueDate
        invoiceNumberToDisplay
        invoiceNumber
        remittanceCodeToDisplay
        paymentDueDate
        ${AppShowInvoicePdfs.fragments.invoiceFields}
        allowedMutations
        buyerContact {
          id
          ${AppBillingContact.fragments.contactFields}
        }
        sellerOrganisation {
          id
          ${AppShowOrganisationName.fragments.organisation}
        }
      }
    `,
    registrationFields: gql`
      fragment _ on Registration {
        ${OrderTable.fragments.registrationFields}
      }
    `,
    paymentFields: gql`
      fragment _ on Payment {
        allowedMutations
        recipientOrganisation {
          id
          name
        }
        buyerContact {
          id
          ${AppBillingContact.fragments.contactFields}
        }
      }
    `
  }
});
</script>
<style lang="scss">
.card-pointer {
  cursor: pointer;
}
.card-disallowed {
  cursor: not-allowed;
}
.not-selected-group {
  filter: grayscale(80%);
  opacity: 0.6;
  .order-content {
    pointer-events: none;
  }
}
.rounded-radio {
  &_content_active::before {
    background: #1269b0;
    border-radius: 24em;
    transform: scale(0.8) translate(-11px, -11px);
    opacity: 1;
    height: 18px;
    width: 18px;
    position: absolute;
    content: " ";
  }

  &_content {
    border-radius: 24em;
    box-shadow: 0px 0px 0px 2px #1269b0;
    margin: 20px 10px;
    width: 18px;
    height: 18px;
  }
}
</style>
<style scoped lang="scss">
.dynamic-title-size {
  font-size: 17px;
  @media (max-width: 576px) {
    font-size: 17px;
  }
}
</style>
